import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import '../styles/pages/AdminDashboard.css';

/**
 * AnnouncementAdminPage - お知らせ管理画面（管理者専用認証）
 */
const AnnouncementAdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // フォームデータ
  const [formData, setFormData] = useState({
    content: '',
    type: 'info',
    link: '',
    linkText: '詳細を見る',
    isActive: true,
    startDate: '',
    endDate: '',
    priority: 0
  });

  // ページタイトル設定
  useEffect(() => {
    document.title = 'お知らせ管理 | つむぎ';
  }, []);

  // 管理者認証を確認
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin');
        return;
      }

      try {
        const response = await api.get('/api/admin/verify', {
          headers: { 'X-Admin-Token': token }
        });
        if (response.data.success) {
          setIsAuthenticated(true);
          loadAnnouncements();
        } else {
          navigate('/admin');
        }
      } catch (error) {
        navigate('/admin');
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // APIリクエストに管理者トークンを付加するヘルパー
  const adminApi = {
    get: (url) => api.get(url, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    post: (url, data) => api.post(url, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    put: (url, data) => api.put(url, data, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    }),
    delete: (url) => api.delete(url, {
      headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
    })
  };

  const loadAnnouncements = async () => {
    try {
      const response = await adminApi.get('/api/announcements/admin');
      if (response.data.success) {
        setAnnouncements(response.data.announcements);
      }
    } catch (error) {
      console.error('お知らせ読み込みエラー:', error);
      showMessage('error', 'お知らせの読み込みに失敗しました');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setFormData({
      content: '',
      type: 'info',
      link: '',
      linkText: '詳細を見る',
      isActive: true,
      startDate: '',
      endDate: '',
      priority: 0
    });
    setShowModal(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      content: announcement.content,
      type: announcement.type,
      link: announcement.link || '',
      linkText: announcement.linkText || '詳細を見る',
      isActive: announcement.isActive,
      startDate: announcement.startDate ? announcement.startDate.slice(0, 16) : '',
      endDate: announcement.endDate ? announcement.endDate.slice(0, 16) : '',
      priority: announcement.priority || 0
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.content.trim()) {
        showMessage('error', 'お知らせ内容を入力してください');
        return;
      }

      const data = {
        ...formData,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        link: formData.link || null
      };

      if (editingAnnouncement) {
        const response = await adminApi.put(`/api/announcements/admin/${editingAnnouncement.id}`, data);
        if (response.data.success) {
          showMessage('success', 'お知らせを更新しました');
          loadAnnouncements();
          closeModal();
        }
      } else {
        const response = await adminApi.post('/api/announcements/admin', data);
        if (response.data.success) {
          showMessage('success', 'お知らせを作成しました');
          loadAnnouncements();
          closeModal();
        }
      }
    } catch (error) {
      console.error('保存エラー:', error);
      showMessage('error', error.response?.data?.error || '保存に失敗しました');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このお知らせを削除しますか？')) return;

    try {
      const response = await adminApi.delete(`/api/announcements/admin/${id}`);
      if (response.data.success) {
        showMessage('success', 'お知らせを削除しました');
        loadAnnouncements();
      }
    } catch (error) {
      console.error('削除エラー:', error);
      showMessage('error', '削除に失敗しました');
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      const response = await adminApi.put(`/api/announcements/admin/${announcement.id}`, {
        isActive: !announcement.isActive
      });
      if (response.data.success) {
        showMessage('success', announcement.isActive ? '非公開にしました' : '公開しました');
        loadAnnouncements();
      }
    } catch (error) {
      console.error('状態更新エラー:', error);
      showMessage('error', '状態の更新に失敗しました');
    }
  };

  const typeLabels = {
    info: { label: '通常', color: '#1565c0', bg: '#e3f2fd' },
    campaign: { label: 'キャンペーン', color: '#7b1fa2', bg: '#f3e5f5' },
    maintenance: { label: 'メンテナンス', color: '#e65100', bg: '#fff3e0' },
    warning: { label: '警告', color: '#c62828', bg: '#ffebee' }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__container">
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'white' }}>
            読み込み中...
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__container">
        <header className="admin-dashboard__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '8px',
                color: 'white'
              }}
            >
              ←
            </button>
            <h1 className="admin-dashboard__title" style={{ fontSize: '1.5rem', margin: 0 }}>お知らせ管理</h1>
          </div>
          <button
            onClick={openCreateModal}
            style={{
              background: 'white',
              color: '#7c3aed',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            + 新規作成
          </button>
        </header>

        {message.text && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              background: message.type === 'success' ? '#d4edda' : '#f8d7da',
              color: message.type === 'success' ? '#155724' : '#721c24'
            }}
          >
            {message.text}
          </div>
        )}

        {announcements.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: '12px',
            color: '#666'
          }}>
            お知らせはまだありません
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  opacity: announcement.isActive ? 1 : 0.6
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: typeLabels[announcement.type].color,
                        background: typeLabels[announcement.type].bg
                      }}
                    >
                      {typeLabels[announcement.type].label}
                    </span>
                    {!announcement.isActive && (
                      <span style={{ fontSize: '12px', color: '#999' }}>非公開</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleActive(announcement)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {announcement.isActive ? '非公開' : '公開'}
                    </button>
                    <button
                      onClick={() => openEditModal(announcement)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #7c3aed',
                        background: 'white',
                        color: '#7c3aed',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #dc3545',
                        background: 'white',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      削除
                    </button>
                  </div>
                </div>

                <p style={{ margin: '0 0 8px', fontSize: '15px', lineHeight: '1.5' }}>
                  {announcement.content}
                </p>

                {announcement.link && (
                  <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#666' }}>
                    リンク: <a href={announcement.link} target="_blank" rel="noopener noreferrer">{announcement.link}</a>
                  </p>
                )}

                <div style={{ fontSize: '12px', color: '#999' }}>
                  {announcement.startDate && (
                    <span>開始: {new Date(announcement.startDate).toLocaleString('ja-JP')} </span>
                  )}
                  {announcement.endDate && (
                    <span>終了: {new Date(announcement.endDate).toLocaleString('ja-JP')} </span>
                  )}
                  <span>優先度: {announcement.priority}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* モーダル */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '16px'
            }}
            onClick={closeModal}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>
                {editingAnnouncement ? 'お知らせを編集' : '新規お知らせ'}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                    内容 <span style={{ color: 'red' }}>*</span>
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="お知らせ内容（200文字以内）"
                    maxLength={200}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      minHeight: '80px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ textAlign: 'right', fontSize: '12px', color: '#999' }}>
                    {formData.content.length}/200
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                    タイプ
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="info">通常（青）</option>
                    <option value="campaign">キャンペーン（紫）</option>
                    <option value="maintenance">メンテナンス（オレンジ）</option>
                    <option value="warning">警告（赤）</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                    リンクURL（任意）
                  </label>
                  <input
                    type="text"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                    リンクテキスト
                  </label>
                  <input
                    type="text"
                    value={formData.linkText}
                    onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                    placeholder="詳細を見る"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      開始日時（任意）
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      終了日時（任意）
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                    優先度
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    min={0}
                    max={100}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    数値が大きいほど優先表示されます
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span style={{ fontSize: '14px' }}>公開する</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={closeModal}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  キャンセル
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#7c3aed',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {editingAnnouncement ? '更新' : '作成'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementAdminPage;
